import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import AnalyticsQueryContainer from 'dashboard/components/AnalyticsQueryContainer';
import RecencyLabel from 'dashboard/components/AnalyticsQueryContainer/RecencyLabel';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import Tooltip from 'dashboard/components/Tooltip';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { optionWithSubtextMapper } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { Period } from 'dashboard/lib/unixTime';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import type { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { MetricsGroupBy } from 'dashboard/models/metrics';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { MetricsFilterParams } from 'dashboard/redux/actions/metrics-inquiry';
import {
  DynamicFilterSet,
  initialState,
  InquiriesQueriedState,
  MetricsState,
} from 'dashboard/redux/reducers/metrics-inquiry';
import InquiryMetrics, {
  FilterDimensions,
} from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/InquiryMetrics';
import { captureMessage } from 'lib/reporting';
import { nameDisplay } from 'lib/utils/template';

const TooltipContainer = styled('div')`
  max-width: 500px;
`;
const SearchAndFilterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: ${Spacing[3]};

  > :nth-child(2) {
    grid-column: span 2;
  }

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    display: flex;
    flex-direction: column;
  }
`;

const TemplatesDropdownContainer = styled.div`
  width: auto;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    width: 100%;
  }
`;

const FilterDropdownContainer = styled.div`
  width: auto;
  grid-row-start: 2;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    width: 100%;
  }
`;

const DatePickerContainer = styled.div`
  min-width: auto;
  grid-column: 1/3;
`;

export interface DashboardInquiryAnalyticsVolumeV2ViewProps
  extends Pick<
    MetricsState,
    | 'volume'
    | 'volumeOverTime'
    | 'filters'
    | 'medianCompletion'
    | 'volumeDelta'
    | 'medianCompletionDelta'
  > {
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleTemplateFilterUpdate: (templateIds: string[]) => void;
  handleTagFilterUpdate: (tagIds: string[]) => void;
  handlePlatformFilterUpdate: (platforms: string[]) => void;
  handleCountryFilterUpdate: (countryCodes: string[]) => void;
  handleDeviceFilterUpdate: (deviceGroups: string[]) => void;
  handleUpdateGranularity: (period: Period) => void;
  lastQueryFilters: MetricsFilterParams;
  overviewPeriod: Period;
  loading: boolean;
  loadVolumeMetrics: (inquiryTemplateIds: string[]) => Promise<void>;
  queriedAt: InquiriesQueriedState;
  templates: (Template | InquiryTemplate)[];
  inquiryTags: Tag[];
  selectedTemplates: (Template | InquiryTemplate)[];
  selectedTags: Tag[];
  errored: boolean;
  handleGroupByFilterUpdateForToggle: (checked: boolean) => void;
  dynamicFilters: DynamicFilterSet;
  loadingDynamicFilters: boolean;
  resetFiltersToInitialState: () => void;
  openCreateQueryTemplateModal: () => void;
  queryTemplates: AnalyticsQueryTemplate[];
  selectedQueryTemplate: AnalyticsQueryTemplate | null;
  handleLoadQueryTemplate: (template: AnalyticsQueryTemplate) => void;
  isQueryTemplateModified: boolean;
  unsetSelectedTemplate: () => void;
}

const DashboardInquiryAnalyticsVolumeV2View = ({
  filters,
  handleDateUpdateV2,
  handleTemplateFilterUpdate,
  handleTagFilterUpdate,
  handlePlatformFilterUpdate,
  handleCountryFilterUpdate,
  handleDeviceFilterUpdate,
  handleUpdateGranularity,
  loadVolumeMetrics,
  loading,
  loadingDynamicFilters,
  queriedAt,
  templates,
  inquiryTags,
  volume,
  volumeDelta,
  volumeOverTime,
  overviewPeriod,
  medianCompletion,
  medianCompletionDelta,
  selectedTemplates,
  selectedTags,
  handleGroupByFilterUpdateForToggle,
  errored,
  lastQueryFilters,
  dynamicFilters,
  resetFiltersToInitialState,
  openCreateQueryTemplateModal,
  queryTemplates,
  handleLoadQueryTemplate,
  selectedQueryTemplate,
  isQueryTemplateModified,
  unsetSelectedTemplate,
}: DashboardInquiryAnalyticsVolumeV2ViewProps) => {
  const { createdAtStart, createdAtEnd, groupBy, deviceGroups, countryCodes, platforms } = filters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  const templateFilterOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: `${nameDisplay(template)} (${template.id})`,
        customLabel: optionWithSubtextMapper(nameDisplay(template), template.id),
        value: template.id,
      })),
    [templates],
  );

  const tagFilterOptions = useMemo(
    () =>
      inquiryTags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [inquiryTags],
  );

  const loaded = useMemo(
    () =>
      queriedAt.volumeOverTime != null &&
      queriedAt.volume != null &&
      volume !== initialState.volume &&
      volumeOverTime !== initialState.volumeOverTime,
    [queriedAt.volumeOverTime, queriedAt.volume, volume, volumeOverTime],
  );

  useEffect(() => {
    if (!loaded && !loading && !errored) {
      loadVolumeMetrics(
        selectedTemplates == null ? [] : selectedTemplates.map((template) => template.id),
      );
    }
  }, [selectedTemplates, loadVolumeMetrics, loaded, loading, errored]);

  const templateFilterLabelTextMapper = useLabelTextMapper(templateFilterOptions, 'templates', {
    emptyIsAll: true,
  });

  const tagFilterLabelTextMapper = useLabelTextMapper(tagFilterOptions, 'tags', {
    emptyIsAll: true,
  });

  const {
    [FilterDimensions.CountryCode]: countryCodesOptions,
    [FilterDimensions.DeviceGroup]: deviceGroupsOptions,
    [FilterDimensions.PlatformIntegration]: platformOptions,
  } = dynamicFilters;

  const sortedCountryCodeOptions = useMemo(
    () => countryCodesOptions.sort((a, b) => (a.value > b.value ? 1 : -1)),
    [countryCodesOptions],
  );

  const sortedDeviceGroupsOptions = useMemo(
    () => deviceGroupsOptions.sort((a, b) => (a.value > b.value ? 1 : -1)),
    [deviceGroupsOptions],
  );

  const sortedPlatformOptions = useMemo(
    () => platformOptions.sort((a, b) => (a.value > b.value ? 1 : -1)),
    [platformOptions],
  );

  const codeFilterLabelTextMapper = useLabelTextMapper(countryCodesOptions, 'countries', {
    emptyIsAll: true,
  });

  const platformFilterLabelTextMapper = useLabelTextMapper(platformOptions, 'integrations', {
    emptyIsAll: true,
  });

  const deviceFilterLabelTextMapper = useLabelTextMapper(deviceGroupsOptions, 'groups', {
    emptyIsAll: true,
  });

  // show interacted rate ONLY if v3 templates are selected
  const filteredTemplates =
    lastQueryFilters.templateOrInquiryTemplateIds.length === 0
      ? templates
      : templates.filter(({ id }) => lastQueryFilters.templateOrInquiryTemplateIds.includes(id));

  // We would only want to show the interacted rate for 3.0 templates since they're associated with sessions
  const showInteractedRate = useMemo(
    () => !filteredTemplates.some(({ type }) => type === 'template'),
    [filteredTemplates],
  );

  const labelSize = 'xs';

  const renderSearchAndFilterWrapper = () => (
    <SearchAndFilterContainer>
      <DatePickerContainer>
        <AnalyticsDatePicker
          dataTest='metrics-inquiries-view__date-range'
          handleDatesChangeV2={handleDateUpdateV2}
          dateRange={dateRange}
          disabled={loading}
        />
      </DatePickerContainer>
      {templates.length > 1 && (
        <TemplatesDropdownContainer>
          <FormControlLabel size={labelSize}>Templates</FormControlLabel>
          <MultiSelect
            size='md'
            value={selectedTemplates.map((template) => template.id)}
            selectOptionValues={templateFilterOptions}
            labelTextMapper={templateFilterLabelTextMapper}
            onChange={handleTemplateFilterUpdate}
            disabled={loading}
            selectAllEnabled
            searchEnabled
          />
        </TemplatesDropdownContainer>
      )}
      <FilterDropdownContainer>
        <FormControlLabel size={labelSize}>Country codes</FormControlLabel>
        <MultiSelect
          size='md'
          value={countryCodes}
          selectOptionValues={sortedCountryCodeOptions}
          labelTextMapper={codeFilterLabelTextMapper}
          onChange={handleCountryFilterUpdate}
          disabled={loading || isEmpty(sortedCountryCodeOptions) || loadingDynamicFilters}
          loading={loadingDynamicFilters}
          searchEnabled
        />
      </FilterDropdownContainer>
      <FilterDropdownContainer>
        <FormControlLabel data-test='metrics-inquiries-view__select-platform' size={labelSize}>
          Platform integrations
        </FormControlLabel>{' '}
        <MultiSelect
          value={platforms}
          selectOptionValues={sortedPlatformOptions}
          labelTextMapper={platformFilterLabelTextMapper}
          onChange={handlePlatformFilterUpdate}
          disabled={loading || isEmpty(sortedPlatformOptions) || loadingDynamicFilters}
          size='md'
          data-test='metrics-inquiries-view__select-platform'
          searchEnabled
          loading={loadingDynamicFilters}
        />
      </FilterDropdownContainer>

      <FilterDropdownContainer>
        <Tooltip
          trigger='hover'
          tooltip={
            <TooltipContainer>
              Mobile (phones and tablets), Desktop, Unknown (could not get any info on device), or
              Other (none of the above)
            </TooltipContainer>
          }
          hideArrow={false}
        >
          <FormControlLabel size={labelSize}>Device groups</FormControlLabel>
        </Tooltip>
        <MultiSelect
          value={deviceGroups}
          selectOptionValues={sortedDeviceGroupsOptions}
          labelTextMapper={deviceFilterLabelTextMapper}
          onChange={handleDeviceFilterUpdate}
          disabled={loading || isEmpty(sortedDeviceGroupsOptions) || loadingDynamicFilters}
          size='md'
          searchEnabled
          loading={loadingDynamicFilters}
        />
      </FilterDropdownContainer>
      <FilterDropdownContainer>
        <FormControlLabel data-test='metrics-inquiries-view__select-tag' size={labelSize}>
          Tags
        </FormControlLabel>
        <MultiSelect
          value={selectedTags.map((template) => template.id)}
          selectOptionValues={tagFilterOptions}
          labelTextMapper={tagFilterLabelTextMapper}
          onChange={handleTagFilterUpdate}
          disabled={loading || isEmpty(tagFilterOptions) || loadingDynamicFilters}
          size='md'
          searchEnabled
          loading={loadingDynamicFilters}
        />
      </FilterDropdownContainer>
    </SearchAndFilterContainer>
  );

  return (
    <>
      <AnalyticsQueryContainer
        selectedQueryTemplate={selectedQueryTemplate}
        queryTemplates={queryTemplates}
        loading={loading}
        onLoad={handleLoadQueryTemplate}
        onRun={() => loadVolumeMetrics(selectedTemplates.map((template) => template.id))}
        onSave={openCreateQueryTemplateModal}
        onClear={resetFiltersToInitialState}
        useGroupByToggle={true}
        groupByValue={groupBy === MetricsGroupBy.Accounts}
        groupByLabel='Group By Accounts'
        groupByOnChange={handleGroupByFilterUpdateForToggle}
        isQueryTemplateModified={isQueryTemplateModified}
        unsetSelectedTemplate={unsetSelectedTemplate}
        recencyLabel={
          <>
            {queriedAt.volume != null && volume?.updated_at && (
              <RecencyLabel queriedAt={queriedAt.volume} recencyIso={volume.updated_at} />
            )}
          </>
        }
      >
        {renderSearchAndFilterWrapper()}
      </AnalyticsQueryContainer>

      {
        // Only display metrics if a query has been run
        loaded && (
          <PersonaErrorBoundary
            onCatch={(error) =>
              captureMessage('Inquiry Analytics failed to render', 'error', {
                error,
                component: 'DashboardInquiryAnalyticsVolumeV2View',
              })
            }
          >
            <InquiryMetrics
              volume={volume}
              volumeDelta={volumeDelta}
              volumeOverTime={volumeOverTime}
              showInteractedRate={showInteractedRate}
              medianCompletion={medianCompletion}
              medianCompletionDelta={medianCompletionDelta}
              overviewPeriod={overviewPeriod}
              handleUpdateGranularity={handleUpdateGranularity}
              lastQueryFilters={lastQueryFilters}
            />
          </PersonaErrorBoundary>
        )
      }
    </>
  );
};

export default DashboardInquiryAnalyticsVolumeV2View;
